import React, { Suspense, useContext, useEffect, useState } from 'react'
import {
  BrowserRouter,
  Route,
  useParams,
  Routes,
  Navigate,
} from 'react-router-dom'

import { LoadingPage, Page404 } from '@dataplace.ai/ui-components/pages'
import { UserSettingsProvider } from 'apps/placeme/src/contexts/UserSettings/UserSettingsContext'
import { RouteChildType, RoutePropsType, RouteType } from '@dataplace.ai/types'
import { AuthContext } from './AuthContext'
import { FlashMessagesProvider } from './FlashMessages'
import LanguageContextProvider from '../contexts/LanguageContext'
import ProductFruitsProvider from './ProductFruitsProvider'

const renderRoutes = (routes: RouteType[]) => routes.map(({
  component: Component,
  guard,
  children,
  rootPath,
  id,
  redirect,
}) => {
  const ParentGuard = guard || React.Fragment

  const params = useParams()

  const parentRoute = redirect || Component
    ? (
      <Route
        key={`route-${id}`}
        element={(
          <ParentGuard>
            {Component && <Component {...params} />}
            {redirect && <Navigate to={redirect} />}
          </ParentGuard>
        )}
        path={rootPath}
      />
    )
    : null

  const childrenRoutes = children
    ? children.map((element: RouteChildType) => {
      const Guard = element.guard || ParentGuard || React.Fragment

      return (
        <Route
          key={`route-${id}-${element.name}`}
          element={(
            <Guard>
              {element.component && <element.component {...params} />}
              {element.redirect && <Navigate to={element.redirect} />}
              <FlashMessagesProvider />
            </Guard>
          )}
          path={`/${(rootPath + element.childPath).replace(/^\/+/, '')}`}
        />
      )
    })
    : []

  return [parentRoute, ...childrenRoutes]
})

export const CustomRoutes: React.FC<RoutePropsType> = (props): JSX.Element => {
  const {
    routes, product, children,
  } = props
  const authContext = useContext(AuthContext)
  const [loaderTimeout, setLoaderTimeout] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => setLoaderTimeout(false), 5000)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (authContext.userData.userLoaded && loaderTimeout) {
      const timer = setTimeout(() => setLoaderTimeout(false), 400)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [authContext.userData.userLoaded])

  return (
    <>
      <BrowserRouter>
        <UserSettingsProvider>
          <LanguageContextProvider>
            {children}
            <Suspense fallback={<LoadingPage product={product} />}>
              <Routes>
                {renderRoutes(routes)}
                <Route
                  element={<Page404 />}
                  path='*'
                />
              </Routes>
            </Suspense>
            {!loaderTimeout && <ProductFruitsProvider />}
          </LanguageContextProvider>
        </UserSettingsProvider>
      </BrowserRouter>
    </>
  )
}
